import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { formatForShortDateAPI } from '../../../utils/dateformatpickr';
import { updateTenderOpeningStatus } from './useUpdateTenderOpeningStatus';
import { buildTenderOpeningDownloadFilename } from '../utils/tenderOpeningHelpers';

const getFilenameFromContentDisposition = contentDisposition => {
  if (!contentDisposition?.includes('filename=')) return null;
  return contentDisposition.split('filename=')[1].replace(/"/g, '').trim();
};

const useHpsRequest = ({ procId, procurement, onRefreshProcurement }) => {
  const [uploadDocument, setUploadDocument] = useState({ items: [] });
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const fetchDocumentUpload = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !procId) return;

    try {
      const response = await fetch(
        `${api.API_URL}/hps/getall-hps-doc-uploaded?ProcurementId=${procId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      const items = (result.data?.items || []).map(item => ({
        id: item.id ?? null,
        documentName: item.documentUpload?.documentName ?? null,
        documentUploadId: item.documentUploadId ?? null,
        actualUrl: item.link ?? null,
      }));
      setUploadDocument({ items });
    } catch {
      void 0;
    }
  }, [procId]);

  useEffect(() => {
    fetchDocumentUpload();
  }, [fetchDocumentUpload]);

  const buildFormData = (formValues, fileAttachment) => {
    const formData = new FormData();
    formData.append('TitleNotaDinas', formValues.titleNotaDinas || '');
    formData.append('NoNotaDinas', formValues.noNotaDinas || '');
    formData.append(
      'DateNotaDinas',
      formValues.hpsRequestDate
        ? formatForShortDateAPI(formValues.hpsRequestDate)
        : ''
    );
    if (fileAttachment) {
      formData.append('DocumentNotaDinas', fileAttachment);
    }
    return formData;
  };

  const saveAsDraftRequestHps = async (formValues, fileAttachment) => {
    const token = getAuthToken();
    if (!token) {
      toast.error('Authentication required. Please login again.');
      return false;
    }

    await axios.put(
      `${api.API_URL}/procurement/save-as-draft-request-hps-market/${procId}`,
      buildFormData(formValues, fileAttachment),
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    await Promise.all([fetchDocumentUpload(), onRefreshProcurement?.()]);
    return true;
  };

  const sendRequestHps = async (formValues, fileAttachment) => {
    const token = getAuthToken();
    if (!token) {
      toast.error('Authentication required. Please login again.');
      return false;
    }

    await axios.put(
      `${api.API_URL}/procurement/send-request-hps/${procId}`,
      buildFormData(formValues, fileAttachment),
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    await updateTenderOpeningStatus(procId, 'isRequestHpsCompleted', {
      maxRetries: 3,
      delayMs: 1000,
    });

    await Promise.all([fetchDocumentUpload(), onRefreshProcurement?.()]);
    return true;
  };

  const exportParcelToExcel = async () => {
    const token = getAuthToken();
    if (!token) {
      toast.error('Authentication required. Please login again.');
      return false;
    }
    if (!procId) {
      toast.error('Procurement ID is required.');
      return false;
    }

    setIsExportingExcel(true);
    try {
      const response = await fetch(
        `${api.API_URL}/procurement/exportToExcelParcelRequestHpsMarket?procurementId=${procId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        let message = `Failed to export parcel data (${response.status}).`;
        try {
          const errorData = await response.json();
          message = errorData?.message || message;
        } catch {
          // response body bukan JSON
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const filename =
        getFilenameFromContentDisposition(
          response.headers.get('Content-Disposition')
        ) ||
        buildTenderOpeningDownloadFilename({
          procurementNumber: procurement?.procurementNumber,
          title: 'Parcel_Request_HPS_Market',
          submittedDate: procurement?.submittedDateTenderOpening,
          extension: 'xlsx',
        });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      const message =
        error?.message || 'Failed to export parcel data to Excel.';
      toast.error(message);
      return false;
    } finally {
      setIsExportingExcel(false);
    }
  };

  return {
    uploadDocument,
    saveAsDraftRequestHps,
    sendRequestHps,
    exportParcelToExcel,
    isExportingExcel,
  };
};

export default useHpsRequest;
